import { useCallback } from 'react';

import type { Credits, CreditsInfo } from '@picsart/miniapp-sdk-types';

import useMount from '@/hooks/useMount';
import { getContext, isContextAvailable } from '@/miniappContext';
import { setAppStore } from '@/store';

/** Subscription payload from `monetization.credits.subscribe`. Some older SDK
 * versions emit `creditInfo` (singular) instead of `creditsInfo` — keep the
 * fallback so we don't drop updates from those clients. */
type CreditsSubscriptionValue = Credits & { creditInfo?: CreditsInfo | null };

const useCredits = () => {
  const subscribeToCredits = useCallback(
    () =>
      getContext().monetization.credits.subscribe(
        (value: CreditsSubscriptionValue) => {
          const info = value?.creditsInfo ?? value?.creditInfo;
          if (!info) return;
          const total = (info.credits ?? 0) + (info.addonCredits ?? 0);
          setAppStore({ availableCredits: total, availableCreditsLoaded: true });
        },
        { needPrevious: true },
      ),
    [],
  );

  useMount(() => {
    if (!isContextAvailable()) return;

    const unsubscribe = subscribeToCredits();

    return () => {
      unsubscribe?.();
    };
  });
};

export default useCredits;
